import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Phone, MapPin, Heart, ChevronLeft, ChevronDown, Globe, Menu, X, Info, Mail, FileText, Calendar, Leaf, PartyPopper, Paperclip } from 'lucide-react';
import { WhatsappIcon, WhatsappShareButton } from 'react-share';
import { Footer } from '../components/shared/Footer';
import { TruncatedText, renderFormattedText } from '../components/shared/TextUtils';
import { getCommuneFromAddress } from '../components/shared/LocationUtils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = "https://aumenu.ch";

export const Restaurant = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [restaurant, setRestaurant] = useState(null);
  const [weekDishes, setWeekDishes] = useState({});
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [weekDays, setWeekDays] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(null);
  const [events, setEvents] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [activeSpecialPages, setActiveSpecialPages] = useState([]);
  const [stickyDate, setStickyDate] = useState('');
  const [selectedDayPopup, setSelectedDayPopup] = useState(null);
  const daySectionsRef = useRef({});

  // Fonction de calcul de distance
  const calcDist = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return null;
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) ** 2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  // Demander la géolocalisation au chargement
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Géolocalisation refusée:', error.message);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }
  }, []);

  useEffect(() => {
    const savedFavorites = localStorage.getItem('restaurantFavorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
    initializeData();
  }, [id]);

  // Rafraîchissement automatique des plats toutes les 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('Rafraîchissement automatique des plats du restaurant...');
      refreshDishes();
    }, 2 * 60 * 1000); // 2 minutes
    
    return () => clearInterval(interval);
  }, [id, weekDays]);

  // Fonction pour rafraîchir uniquement les plats (sans recharger le restaurant)
  const refreshDishes = async () => {
    if (weekDays.length === 0 || !restaurant) return;
    
    try {
      const dishesPromises = weekDays.map(date => 
        axios.get(`${API}/dishes?date=${date}`).then(res => ({ date, dishes: res.data }))
      );
      
      const allDishes = await Promise.all(dishesPromises);
      
      const dishesByDate = {};
      allDishes.forEach(({ date, dishes }) => {
        const mine = dishes.filter(d => d.restaurant_id === restaurant._id);
        const seen = new Set();
        const daily = [];
        const suggestions = [];
        mine.forEach(dish => {
          if (dish.is_suggestion || (dish.valid_until && dish.date !== date)) {
            suggestions.push(dish);
          } else {
            daily.push(dish);
            seen.add(dish.name.toLowerCase());
          }
        });
        const uniqueSuggestions = suggestions.filter(d => !seen.has(d.name.toLowerCase()));
        daily.sort((a, b) => {
          if (!a.valid_until && b.valid_until) return -1;
          if (a.valid_until && !b.valid_until) return 1;
          return 0;
        });
        dishesByDate[date] = [...daily, ...uniqueSuggestions];
      });
      
      setWeekDishes(dishesByDate);
    } catch (error) {
      console.error('Erreur rafraîchissement:', error);
    }
  };

  // Formate une date en YYYY-MM-DD en utilisant l'heure locale (pas UTC)
  const getLocalDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Génère les jours depuis une date jusqu'à 7 jours après
  const getDaysFromDate = (startDate) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(getLocalDateStr(date));
    }
    return days;
  };

  // Calcule la date à afficher selon l'heure (après 14h = demain)
  const getDisplayDate = () => {
    const now = new Date();
    if (now.getHours() >= 14) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow;
    }
    return now;
  };

  const initializeData = async () => {
    // Toujours calculer la date basée sur l'heure actuelle
    const displayDate = getDisplayDate();
    const displayDateStr = getLocalDateStr(displayDate);
    setSelectedDate(displayDateStr);
    
    // Jours à partir de la date sélectionnée
    const days = getDaysFromDate(displayDate);
    setWeekDays(days);

    try {
      // Charger le restaurant par slug ou ID
      const restaurantRes = await axios.get(`${API}/restaurants/${id}`);
      const foundRestaurant = restaurantRes.data;
      setRestaurant(foundRestaurant);
      const restaurantId = foundRestaurant._id;

      // Charger les plats pour toute la semaine
      const dishesPromises = days.map(date => 
        axios.get(`${API}/dishes?date=${date}`).then(res => ({ date, dishes: res.data }))
      );
      
      const allDishes = await Promise.all(dishesPromises);
      
      // Organiser par date, trier et dédupliquer
      const dishesByDate = {};
      allDishes.forEach(({ date, dishes }) => {
        const mine = dishes.filter(d => d.restaurant_id === restaurantId);
        // Tri : plats du jour en premier, puis suggestions
        const seen = new Set();
        const daily = [];
        const suggestions = [];
        mine.forEach(dish => {
          if (dish.is_suggestion || (dish.valid_until && dish.date !== date)) {
            suggestions.push(dish);
          } else {
            daily.push(dish);
            seen.add(dish.name.toLowerCase());
          }
        });
        const uniqueSuggestions = suggestions.filter(d => !seen.has(d.name.toLowerCase()));
        daily.sort((a, b) => {
          if (!a.valid_until && b.valid_until) return -1;
          if (a.valid_until && !b.valid_until) return 1;
          return 0;
        });
        dishesByDate[date] = [...daily, ...uniqueSuggestions];
      });
      
      setWeekDishes(dishesByDate);

      // Charger les événements à venir pour ce restaurant
      const eventsRes = await axios.get(`${API}/events?restaurant_id=${restaurantId}&upcoming=true`);
      setEvents(eventsRes.data);

      // Charger les pages spéciales actives
      try {
        const spRes = await axios.get(`${API}/special-pages?active_only=true`);
        setActiveSpecialPages(spRes.data);
      } catch (e) { console.error('Events load failed:', e); }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = (restaurantId) => {
    const newFavorites = favorites.includes(restaurantId)
      ? favorites.filter(fid => fid !== restaurantId)
      : [...favorites, restaurantId];
    setFavorites(newFavorites);
    localStorage.setItem('restaurantFavorites', JSON.stringify(newFavorites));
  };

  // Scroll listener pour la pastille qui se déplace
  useEffect(() => {
    const handleScroll = () => {
      const sections = Object.entries(daySectionsRef.current)
        .filter(([, el]) => el)
        .sort(([a], [b]) => a.localeCompare(b));
      let currentDate = '';
      for (const [date, el] of sections) {
        const rect = el.getBoundingClientRect();
        // Premier jour dont le label de date est visible sous le header (~140px)
        if (rect.top >= 100) {
          currentDate = date;
          break;
        }
      }
      // Si aucun jour n'a son top >= 100, prendre le dernier visible
      if (!currentDate) {
        for (const [date, el] of sections) {
          const rect = el.getBoundingClientRect();
          if (rect.bottom > 160) {
            currentDate = date;
            break;
          }
        }
      }
      setStickyDate(currentDate);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const registerDayRef = useCallback((date, el) => {
    daySectionsRef.current[date] = el;
  }, []);

  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const isToday = (dateStr) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    return dateStr === todayStr;
  };

  const isTomorrow = (dateStr) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    return dateStr === tomorrowStr;
  };

  // Pour les jours suivants, afficher la date complète avec indication si c'est aujourd'hui/demain
  const getSecondaryDateLabel = (dateStr) => {
    const fullDate = formatFullDate(dateStr);
    if (isToday(dateStr)) return `Aujourd'hui - ${fullDate}`;
    if (isTomorrow(dateStr)) return `Demain - ${fullDate}`;
    return fullDate;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F0]">
        <div className="text-center">
          <div className="animate-pulse mb-6">
            <h1 className="text-4xl font-bold text-warmgray-dark">
              AUMENU<span className="text-terracotta">.CH</span>
            </h1>
          </div>
          <div className="flex justify-center gap-2">
            <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
          </div>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F0]">
        <div className="text-center text-warmgray-dark">
          <p className="text-xl mb-4">Restaurant non trouvé</p>
          <Link to="/plats" className="text-terracotta hover:underline">Retour aux plats</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-[#F0E6D8]" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
      
      {/* ====== HERO SECTION — Fond ardoise ====== */}
      <div className="relative z-10">
        <div 
          className="relative overflow-hidden"
          style={{ 
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_cef8a789-684e-42da-8da6-08c3769b942a/artifacts/02czd85l_blackboard%20texture%200102.jpg)`, 
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '200px'
          }}
        >

          {/* Navigation top */}
          <div className="relative z-10 grid grid-cols-3 items-center px-4 pt-4 pb-2">
            <button onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/plats')} className="flex items-center gap-1.5 text-white/70 hover:text-[#D4AF37] transition-colors" data-testid="back-button">
              <ChevronLeft className="h-5 w-5" />
              <span className="text-sm">Retour</span>
            </button>
            <Link to="/" className="text-sm font-bold text-white/60 text-center">
              AUMENU<span className="text-[#D4AF37]">.CH</span>
            </Link>
            <div className="flex items-center justify-end gap-2">
              <WhatsappShareButton
                url={`${SITE_URL}/restaurant/${restaurant.slug || id}`}
                title={events.length > 0 
                  ? `${events[0].title} chez ${restaurant.name}, ça te dit ?`
                  : `Regarde les plats du ${restaurant.name}, ça a l'air trop bon !`
                }
                separator=" "
              >
                <WhatsappIcon size={28} round data-testid="whatsapp-share-btn" />
              </WhatsappShareButton>
            </div>
          </div>
          
          {/* Nom du restaurant — GRAND */}
          <div className="relative z-10 text-center px-6 pt-6 pb-16">
            {/* Filets dorés décoratifs */}
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-8 bg-gradient-to-r from-transparent to-[#D4AF37]/60"></div>
              <span className="text-[#D4AF37] text-lg">✦</span>
              <div className="h-px w-8 bg-gradient-to-l from-transparent to-[#D4AF37]/60"></div>
            </div>
            
            <h1 
              className="text-3xl md:text-5xl font-extrabold text-white leading-tight"
              data-testid="restaurant-name"
            >
              {restaurant.name}
            </h1>
            
            {/* Commune */}
            <p className="mt-3 text-white/50 text-sm flex items-center justify-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {restaurant.commune || getCommuneFromAddress(restaurant.address, restaurant.postal_code)}
              {userLocation && restaurant.lat && restaurant.lng && (
                <span className="text-[#D4AF37] font-semibold ml-1">
                  · {calcDist(userLocation.lat, userLocation.lng, restaurant.lat, restaurant.lng)?.toFixed(1)} km
                </span>
              )}
            </p>
          </div>
        </div>

        {/* ====== BARRE D'ACTIONS FLOTTANTE — À cheval ardoise/beige ====== */}
        <div className="relative z-20 -mt-8 px-4">
          <div 
            className="mx-auto max-w-md rounded-2xl border border-stone-200 px-4 py-3 flex items-center justify-around gap-2"
            style={{ 
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
            }}
            data-testid="quick-action-bar"
          >
            {/* Plan */}
            <a 
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address)}`}
              target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 text-[#1A1C23] hover:text-[#D4AF37] transition-colors"
              data-testid="action-map"
            >
              <MapPin className="h-5 w-5" />
              <span className="text-[10px] font-medium">Plan</span>
            </a>
            
            {/* Appeler */}
            <a 
              href={`tel:${restaurant.phone}`}
              className="flex flex-col items-center gap-1 text-[#1A1C23] hover:text-[#D4AF37] transition-colors"
              data-testid="action-call"
            >
              <Phone className="h-5 w-5" />
              <span className="text-[10px] font-medium">Appeler</span>
            </a>
            
            {/* Site web */}
            {restaurant.website ? (
              <a 
                href={restaurant.website.startsWith('http') ? restaurant.website : `https://${restaurant.website}`}
                target="_blank" rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 text-[#1A1C23] hover:text-[#D4AF37] transition-colors"
                data-testid="action-website"
              >
                <Globe className="h-5 w-5" />
                <span className="text-[10px] font-medium">Site web</span>
              </a>
            ) : (
              <div className="flex flex-col items-center gap-1 text-[#1A1C23]/30">
                <Globe className="h-5 w-5" />
                <span className="text-[10px] font-medium">Site web</span>
              </div>
            )}
            
            {/* Favori */}
            <button 
              onClick={() => toggleFavorite(restaurant._id)}
              className="flex flex-col items-center gap-1 transition-colors"
              data-testid="action-favorite"
            >
              <Heart className={`h-5 w-5 ${favorites.includes(restaurant._id) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#1A1C23] hover:text-[#D4AF37]'}`} />
              <span className="text-[10px] font-medium text-[#1A1C23]">Favori</span>
            </button>
          </div>
        </div>
      </div>

      {/* ====== CONTENU PRINCIPAL — Fond beige chaud ====== */}
      <main className="relative z-10 bg-[#F0E6D8] pt-8 pb-12 px-4" style={{ minHeight: '50vh' }}>
        <div className="max-w-2xl mx-auto space-y-6">

          {/* ====== ÉVÉNEMENTS ====== */}
          {events.length > 0 && events
            .filter(event => {
              if (activeSpecialPages && activeSpecialPages.length > 0) {
                return !activeSpecialPages.some(sp => 
                  event.date >= sp.start_date && event.date <= sp.end_date
                );
              }
              return true;
            })
            .map(event => (
            <div 
              key={event._id}
              className="relative rounded-2xl shadow-lg border-2 border-[#D4AF37]/40 overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #FFF8E7 0%, #FFF3D0 100%)' }}
              data-testid="event-card"
            >
              <button
                onClick={() => setExpandedEvents(prev => ({ ...prev, [event._id]: !prev[event._id] }))}
                className="w-full p-4 text-left"
                data-testid="event-toggle"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-warmgray-dark text-center">{event.title}</h3>
                    <p className="text-xs font-semibold text-[#8B6914] text-center mt-1">
                      {(event.end_date && event.end_date !== event.date)
                        ? `${new Date(event.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} → ${new Date(event.end_date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`
                        : new Date(event.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
                      }
                      {event.start_time && ` · ${event.start_time}${event.end_time ? ` - ${event.end_time}` : ''}`}
                    </p>
                    {event.price > 0 && (
                      <p className="text-xs font-bold text-[#8B6914] mt-1 text-center">
                        CHF {Number.isInteger(event.price) ? event.price + '.-' : event.price.toFixed(2)}
                      </p>
                    )}
                  </div>
                  <ChevronDown className={`h-5 w-5 text-[#D4AF37] flex-shrink-0 transition-transform duration-200 ${expandedEvents[event._id] ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {expandedEvents[event._id] && (
                <div className="px-4 pb-4 pt-0 border-t border-[#D4AF37]/20 space-y-1.5">
                  {event.description && <p className="text-sm text-warmgray mt-2">{renderFormattedText(event.description)}</p>}
                  {event.menu && <p className={`text-sm text-warmgray-dark whitespace-pre-line ${event.is_menu_centered ? 'text-center' : ''}`}>{renderFormattedText(event.menu)}</p>}
                  {event.flyer_url && (
                    <a 
                      href={`${BACKEND_URL}${event.flyer_url}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/50 text-[#8B6914] text-sm font-medium hover:bg-[#D4AF37]/30 transition-colors"
                      data-testid="event-flyer-link"
                    >
                      <Paperclip className="h-4 w-4" /> Voir le flyer
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* ====== PLAT DU JOUR EN EXERGUE (premier jour) ====== */}
          {weekDays.length > 0 && (() => {
            const mainDate = weekDays[0];
            const mainDishes = weekDishes[mainDate] || [];
            const dateObj = new Date(mainDate + 'T12:00:00');
            const dayOfWeek = dateObj.getDay();
            const closedDayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            const isClosed = restaurant.closed_days?.includes(closedDayIndex);
            const isTemporaryClosure = restaurant.temporary_closure === true;
            const isOnVacation = restaurant.vacation_start && restaurant.vacation_end &&
              new Date(mainDate) >= new Date(restaurant.vacation_start) && new Date(mainDate) <= new Date(restaurant.vacation_end);
            const dayNames = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];

            return (
              <div 
                className="bg-white rounded-2xl p-6 border border-stone-200"
                style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.04)' }}
                ref={(el) => registerDayRef(mainDate, el)}
                data-date={mainDate}
                data-testid="featured-dish-card"
              >
                {/* En-tête */}
                <div className="text-center mb-5">
                  <p className="text-[#8B6914] text-[10px] font-semibold tracking-[0.25em] uppercase mb-1">
                    {isToday(mainDate) ? "Plat du jour" : isTomorrow(mainDate) ? "Au menu demain" : "Au menu"}
                  </p>
                  <p className="text-[#1A1C23] text-lg font-bold capitalize">
                    {formatFullDate(mainDate)}
                  </p>
                  {/* Filet doré décoratif */}
                  <div className="flex items-center justify-center gap-3 mt-2">
                    <div className="h-px w-8 bg-gradient-to-r from-transparent to-[#8B6914]/40"></div>
                    <span className="text-[#8B6914] text-sm">✦</span>
                    <div className="h-px w-8 bg-gradient-to-l from-transparent to-[#8B6914]/40"></div>
                  </div>
                </div>
                
                {/* Contenu */}
                {isTemporaryClosure ? (
                  <div className="text-center py-4 px-3 rounded-xl bg-orange-50 border border-orange-200">
                    <p className="text-orange-700 font-medium italic">Fermeture temporaire</p>
                  </div>
                ) : isOnVacation ? (
                  <div className="text-center py-4 px-3 rounded-xl bg-stone-50 border border-stone-200">
                    <p className="text-stone-500 italic">{restaurant.vacation_reason || 'En vacances'}</p>
                  </div>
                ) : isClosed ? (
                  <div className="text-center py-4 px-3 rounded-xl bg-stone-50 border border-stone-200">
                    <p className="text-stone-500 italic">Fermé le {dayNames[dayOfWeek]}</p>
                  </div>
                ) : mainDishes.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-stone-400 italic text-sm">Menu pas encore communiqué</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {mainDishes.map((dish) => (
                      <div key={dish._id} className="py-2 border-b border-stone-100 last:border-0">
                        {(dish.is_title_centered || dish.is_centered) ? (
                          <div className="text-center">
                            <h4 className="text-base font-extrabold text-[#1A1C23] flex items-center justify-center gap-1.5">
                              <TruncatedText text={dish.name} />
                              {dish.is_vegetarian && <Leaf className="h-4 w-4 text-green-600" />}
                              {dish.is_menu && <PartyPopper className="h-4 w-4 text-amber-600" />}
                              {dish.flyer_url && <a href={`${BACKEND_URL}${dish.flyer_url}`} target="_blank" rel="noopener noreferrer"><Paperclip className="h-4 w-4 text-amber-600 hover:text-amber-800" /></a>}
                            </h4>
                            {dish.price > 0 && (
                              <p className="text-[#D4AF37] font-bold text-sm mt-0.5">
                                CHF {Number.isInteger(dish.price) ? dish.price + '.-' : dish.price.toFixed(2)}
                              </p>
                            )}
                            {dish.description && <p className={`text-stone-500 text-xs italic mt-1 ${(dish.is_description_centered || dish.is_centered) ? 'text-center' : ''}`}><TruncatedText text={dish.description} /></p>}
                          </div>
                        ) : (
                          <div className="flex justify-between items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-base font-extrabold text-[#1A1C23] flex items-center gap-1.5">
                                <TruncatedText text={dish.name} />
                                {dish.is_vegetarian && <Leaf className="h-4 w-4 text-green-600 flex-shrink-0" />}
                                {dish.is_menu && <PartyPopper className="h-4 w-4 text-amber-600 flex-shrink-0" />}
                                {dish.flyer_url && <a href={`${BACKEND_URL}${dish.flyer_url}`} target="_blank" rel="noopener noreferrer"><Paperclip className="h-4 w-4 text-amber-600 flex-shrink-0 hover:text-amber-800" /></a>}
                              </h4>
                              {dish.description && <p className={`text-stone-500 text-xs italic mt-0.5 ${(dish.is_description_centered || dish.is_centered) ? 'text-center' : ''}`}><TruncatedText text={dish.description} /></p>}
                              {dish.valid_until && <p className="mt-1 text-[10px] text-[#D4AF37]">Jusqu'au {new Date(dish.valid_until + 'T00:00:00').toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}</p>}
                              {dish.is_suggestion && <p className="mt-1 text-[10px] text-[#D4AF37]">Suggestion du moment</p>}
                            </div>
                            {dish.price > 0 && (
                              <span className="text-[#D4AF37] font-bold text-sm shrink-0">
                                CHF {Number.isInteger(dish.price) ? dish.price + '.-' : dish.price.toFixed(2)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Note menu */}
                {restaurant.menu_note && (
                  <p className="text-xs text-stone-400 italic text-center mt-4 pt-3 border-t border-stone-100">{restaurant.menu_note}</p>
                )}
              </div>
            );
          })()}

          {/* ====== JOURS SUIVANTS — Scroll horizontal ====== */}
          {weekDays.length > 1 && (() => {
            const futureDays = weekDays.slice(1);
            if (futureDays.length === 0) return null;

            return (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-px flex-1 bg-stone-300/50"></div>
                  <span className="text-xs font-semibold text-stone-500 tracking-wider uppercase">Jours suivants</span>
                  <div className="h-px flex-1 bg-stone-300/50"></div>
                </div>
                
                <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory hide-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
                  <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
                  {futureDays.map((date) => {
                    const dishes = weekDishes[date] || [];
                    const dateObj = new Date(date + 'T12:00:00');
                    const dayOfWeek = dateObj.getDay();
                    const closedDayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                    const isClosed = restaurant.closed_days?.includes(closedDayIndex);
                    const isOnVacationDay = restaurant.vacation_start && restaurant.vacation_end &&
                      new Date(date) >= new Date(restaurant.vacation_start) && new Date(date) <= new Date(restaurant.vacation_end);
                    const dayNames = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];
                    const hasDishes = dishes.length > 0 && !isClosed && !isOnVacationDay;

                    return (
                      <div 
                        key={date}
                        ref={(el) => registerDayRef(date, el)}
                        data-date={date}
                        className={`snap-start shrink-0 w-[200px] bg-white rounded-xl p-4 border transition-all ${hasDishes ? 'border-stone-200 cursor-pointer hover:border-[#D4AF37]/40 active:scale-[0.98]' : 'border-stone-100'}`}
                        style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.03)' }}
                        onClick={() => hasDishes && setSelectedDayPopup(date)}
                        data-testid={`day-card-${date}`}
                      >
                        {/* Date */}
                        <div className="text-center mb-2">
                          <span className={`text-lg font-bold ${hasDishes ? 'text-[#8B6914]' : 'text-stone-400'}`}>
                            {dayNames[dayOfWeek]} {dateObj.getDate()}
                          </span>
                        </div>
                        
                        {/* Contenu */}
                        {isOnVacationDay ? (
                          <p className="text-stone-400 text-xs italic text-center">{restaurant.vacation_reason || 'En vacances'}</p>
                        ) : isClosed ? (
                          <p className="text-stone-400 text-xs italic text-center">Fermé</p>
                        ) : dishes.length === 0 ? (
                          <p className="text-stone-400 text-xs italic text-center">À venir</p>
                        ) : (
                          <div className="space-y-1.5">
                            {dishes.slice(0, 3).map((dish) => (
                              <div key={dish._id} className="text-xs">
                                <p className="text-[#1A1C23] font-medium truncate">{dish.name}</p>
                                {dish.price > 0 && (
                                  <p className="text-[#D4AF37] font-semibold">CHF {Number.isInteger(dish.price) ? dish.price + '.-' : dish.price.toFixed(2)}</p>
                                )}
                              </div>
                            ))}
                            {dishes.length > 3 && (
                              <p className="text-[10px] text-stone-400">+{dishes.length - 3} autre{dishes.length - 3 > 1 ? 's' : ''}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ====== INFOS PRATIQUES ====== */}
          {(restaurant.closed_days?.length > 0 || restaurant.dogs_allowed || restaurant.vegetarian_menu || restaurant.takeaway || restaurant.address) && (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-stone-300/50"></div>
                <span className="text-xs font-semibold text-stone-500 tracking-wider uppercase">Infos pratiques</span>
                <div className="h-px flex-1 bg-stone-300/50"></div>
              </div>
              
              <div className="bg-white rounded-2xl p-5 border border-stone-200 space-y-3" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.03)' }} data-testid="amenities-section">
                {/* Adresse complète */}
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-stone-600 hover:text-[#D4AF37] transition-colors"
                >
                  <MapPin className="h-4 w-4 text-[#D4AF37] shrink-0" />
                  <span>{restaurant.address}</span>
                </a>

                {/* Téléphone */}
                <a href={`tel:${restaurant.phone}`} className="flex items-center gap-3 text-sm text-stone-600 hover:text-[#D4AF37] transition-colors">
                  <Phone className="h-4 w-4 text-[#D4AF37] shrink-0" />
                  <span>{restaurant.phone}</span>
                </a>

                {/* Jours de fermeture */}
                {restaurant.closed_days?.length > 0 && (
                  <div className="flex items-center gap-3 text-sm text-stone-600">
                    <Calendar className="h-4 w-4 text-[#D4AF37] shrink-0" />
                    <span>Fermé le {restaurant.closed_days.map(d => ['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche'][d]).join(', ')}</span>
                  </div>
                )}

                {/* Options */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {restaurant.vegetarian_menu && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                      <Leaf className="h-3.5 w-3.5" /> Végétarien
                    </span>
                  )}
                  {restaurant.dogs_allowed && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                      🐕 Chiens {restaurant.dogs_allowed === 'both' ? 'acceptés' : restaurant.dogs_allowed === 'terrasse' ? 'en terrasse' : 'en salle'}
                    </span>
                  )}
                  {restaurant.takeaway && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                      📦 À emporter
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* ====== POP-UP JOUR SUIVANT ====== */}
      {selectedDayPopup && (() => {
        const popupDishes = weekDishes[selectedDayPopup] || [];
        const popupDate = new Date(selectedDayPopup + 'T12:00:00');
        const dayNames = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDayPopup(null)}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
            <div 
              className="relative bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto border border-stone-200 p-6"
              style={{ boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }}
              onClick={(e) => e.stopPropagation()}
              data-testid="day-popup"
            >
              {/* Bouton fermer */}
              <button 
                onClick={() => setSelectedDayPopup(null)}
                className="absolute top-3 right-3 text-stone-400 hover:text-stone-600 transition-colors"
                data-testid="day-popup-close"
              >
                <X className="h-5 w-5" />
              </button>

              {/* En-tête */}
              <div className="text-center mb-5">
                <p className="text-[#8B6914] text-[10px] font-semibold tracking-[0.25em] uppercase mb-1">
                  {isToday(selectedDayPopup) ? "Plat du jour" : isTomorrow(selectedDayPopup) ? "Au menu demain" : "Au menu"}
                </p>
                <p className="text-[#1A1C23] text-lg font-bold capitalize">
                  {formatFullDate(selectedDayPopup)}
                </p>
              </div>

              {/* Plats */}
              <div className="space-y-3">
                {popupDishes.map((dish) => (
                  <div key={dish._id} className="py-2 border-b border-stone-100 last:border-0">
                    {(dish.is_title_centered || dish.is_centered) ? (
                      <div className="text-center">
                        <h4 className="text-base font-extrabold text-[#1A1C23] flex items-center justify-center gap-1.5">
                          <TruncatedText text={dish.name} />
                          {dish.is_vegetarian && <Leaf className="h-4 w-4 text-green-600" />}
                          {dish.is_menu && <PartyPopper className="h-4 w-4 text-amber-600" />}
                        </h4>
                        {dish.price > 0 && (
                          <p className="text-[#D4AF37] font-bold text-sm mt-0.5">
                            CHF {Number.isInteger(dish.price) ? dish.price + '.-' : dish.price.toFixed(2)}
                          </p>
                        )}
                        {dish.description && <p className={`text-stone-500 text-xs italic mt-1 ${(dish.is_description_centered || dish.is_centered) ? 'text-center' : ''}`}><TruncatedText text={dish.description} /></p>}
                      </div>
                    ) : (
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base font-extrabold text-[#1A1C23] flex items-center gap-1.5">
                            <TruncatedText text={dish.name} />
                            {dish.is_vegetarian && <Leaf className="h-4 w-4 text-green-600 flex-shrink-0" />}
                            {dish.is_menu && <PartyPopper className="h-4 w-4 text-amber-600 flex-shrink-0" />}
                          </h4>
                          {dish.description && <p className={`text-stone-500 text-xs italic mt-0.5 ${(dish.is_description_centered || dish.is_centered) ? 'text-center' : ''}`}><TruncatedText text={dish.description} /></p>}
                          {dish.is_suggestion && <p className="mt-1 text-[10px] text-[#8B6914]">Suggestion du moment</p>}
                        </div>
                        {dish.price > 0 && (
                          <span className="text-[#D4AF37] font-bold text-sm shrink-0">
                            CHF {Number.isInteger(dish.price) ? dish.price + '.-' : dish.price.toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Footer avec fond ardoise */}
      <div 
        className="relative z-10"
        style={{ 
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_cef8a789-684e-42da-8da6-08c3769b942a/artifacts/02czd85l_blackboard%20texture%200102.jpg)`, 
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <Footer showPrivacyLink={false} />
      </div>
    </div>
  );
};
